import { useState } from 'react';
import { Icons } from '../Icons';
import { biometricAuth } from '../../services/biometricAuth';
import './BiometricSetup.css';

interface BiometricSetupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function BiometricSetup({ isOpen, onClose, onSuccess }: BiometricSetupProps) {
  const [isRegistering, setIsRegistering] = useState(false);
  const [lockOnStart, setLockOnStart] = useState(biometricAuth.getLockOnStart());
  const [error, setError] = useState<string | null>(null);
  const isEnabled = biometricAuth.isBiometricEnabled();
  const isSupported = biometricAuth.isBiometricSupported();

  // Detect if using Chrome (which doesn't support Touch ID on macOS)
  const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
  const isMac = /Mac/.test(navigator.platform);

  // Debug logging
  console.log('🔐 BiometricSetup modal state:', {
    isOpen,
    isEnabled,
    isSupported,
    isRegistering,
  });

  if (!isOpen) {
    console.log('🔐 BiometricSetup: Modal closed, returning null');
    return null;
  }

  console.log('🔐 BiometricSetup: Modal OPEN - Rendering overlay and modal');

  const handleRegister = async () => {
    console.log('🔐 handleRegister called - Starting registration flow');
    setIsRegistering(true);
    setError(null);

    try {
      console.log('🔐 Calling biometricAuth.register()...');
      const success = await biometricAuth.register();
      console.log('🔐 Registration result:', success);

      if (success) {
        if (onSuccess) onSuccess();
        onClose();
      } else {
        setError('Échec de l\'enregistrement biométrique. Veuillez réessayer.');
      }
    } catch (err) {
      console.error('❌ handleRegister caught error:', err);
      setError('Erreur lors de l\'enregistrement. Vérifiez que vous avez autorisé l\'accès.');
      console.error('Biometric registration error:', err);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleDisable = () => {
    if (confirm('Désactiver l\'authentification biométrique ?')) {
      biometricAuth.disable();
      onClose();
    }
  };

  const handleToggleLockOnStart = (enabled: boolean) => {
    setLockOnStart(enabled);
    biometricAuth.setLockOnStart(enabled);
  };

  if (!isSupported) {
    return (
      <div className="biometric-modal-overlay" onClick={onClose}>
        <div className="biometric-setup-modal" onClick={e => e.stopPropagation()}>
          <div className="biometric-setup-header">
            <h2>Authentification Biométrique</h2>
            <button className="close-btn" onClick={onClose} aria-label="Close">
              <Icons.X size={24} />
            </button>
          </div>

          <div className="biometric-setup-body">
            <div className="biometric-not-supported">
              <Icons.AlertCircle size={64} color="#ef4444" />
              <h3>Non Supporté</h3>
              <p>
                Votre appareil ou navigateur ne supporte pas l'authentification biométrique (WebAuthn).
              </p>
              <p className="hint">
                Essayez d'utiliser Chrome, Edge, ou Safari sur un appareil compatible Touch ID / Face ID.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="biometric-modal-overlay" onClick={onClose}>
      <div className="biometric-setup-modal" onClick={e => e.stopPropagation()}>
        <div className="biometric-setup-header">
          <h2>Authentification Biométrique</h2>
          <button className="close-btn" onClick={onClose}>
            <Icons.X size={24} />
          </button>
        </div>

        <div className="biometric-setup-body">
          {!isEnabled ? (
            <>
              <div className="biometric-icon">
                <Icons.Lock size={64} />
              </div>

              <h3>Sécurisez votre Portfolio</h3>
              <p className="description">
                Activez Touch ID ou Face ID pour protéger l'accès à votre portfolio avec votre empreinte digitale ou reconnaissance faciale.
              </p>

              {/* Chrome on macOS warning */}
              {isChrome && isMac && (
                <div className="error-message" style={{ background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)', color: '#f59e0b' }}>
                  <Icons.AlertCircle size={18} />
                  <div style={{ textAlign: 'left' }}>
                    <strong>⚠️ Chrome ne supporte pas Touch ID sur macOS</strong>
                    <br />
                    <small>Utilisez Safari pour une meilleure expérience biométrique. Sur Chrome, vous devrez utiliser le déverrouillage manuel.</small>
                  </div>
                </div>
              )}

              <div className="biometric-features">
                <div className="feature-item">
                  <Icons.CheckCircle size={20} color="#22c55e" />
                  <span>Connexion rapide et sécurisée</span>
                </div>
                <div className="feature-item">
                  <Icons.CheckCircle size={20} color="#22c55e" />
                  <span>Aucun mot de passe à retenir</span>
                </div>
                <div className="feature-item">
                  <Icons.CheckCircle size={20} color="#22c55e" />
                  <span>Protection biométrique locale</span>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  <Icons.AlertCircle size={18} />
                  {error}
                </div>
              )}

              <button
                className="setup-btn primary"
                onClick={handleRegister}
                disabled={isRegistering}
              >
                {isRegistering ? (
                  <>
                    <Icons.Loader size={20} className="spinner" />
                    Configuration en cours...
                  </>
                ) : (
                  <>
                    <Icons.Fingerprint size={20} />
                    Activer la Biométrie
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <div className="biometric-icon enabled">
                <Icons.CheckCircle size={64} color="#22c55e" />
              </div>

              <h3>Biométrie Activée</h3>
              <p className="description">
                Votre portfolio est protégé par authentification biométrique.
              </p>

              <div className="biometric-settings">
                <div className="setting-item">
                  <div className="setting-info">
                    <Icons.Lock size={20} />
                    <div>
                      <div className="setting-label">Verrouiller au démarrage</div>
                      <div className="setting-hint">Demander l'authentification à chaque ouverture</div>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={lockOnStart}
                      onChange={e => handleToggleLockOnStart(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <button className="setup-btn danger" onClick={handleDisable}>
                <Icons.Trash2 size={20} />
                Désactiver la Biométrie
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
